import type {
  BillingPeriod,
  EntityStatus,
  PlanCategory,
  TargetAudience,
} from '@monorepo/shared/schemas/common'
import { CHARGE_TYPE } from '@monorepo/shared/schemas/common'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  addonService,
  type CreatePlan,
  type CreatePlanAgentAction,
  type CreatePlanCharge,
  type CreatePlanFeature,
  type CreatePricingTier,
  type PlanQuery,
  type UpdatePlan,
  type UpdatePlanAgentAction,
  type UpdatePlanCharge,
  type UpdatePlanFeature,
  type UpdatePricingTier,
} from '../services/addonService'
import type { PlanFilters } from '../types/addon.types'

interface ApiError {
  response?: {
    data?: {
      error?: {
        message?: string
      }
      message?: string
    }
  }
  message?: string
}

export const ADDONS_QUERY_KEY = 'addons'

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Get all addons with optional filters
 */
export function useAddons(query?: PlanQuery) {
  return useQuery({
    queryKey: [ADDONS_QUERY_KEY, query],
    queryFn: () => addonService.getAddons(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Get single addon by ID (includes nested charges, features, agent actions)
 */
export function useAddon(id: string) {
  return useQuery({
    queryKey: [ADDONS_QUERY_KEY, id],
    queryFn: () => addonService.getAddon(id),
    enabled: !!id,
  })
}

// ============================================================================
// MUTATION HOOKS - PLANS
// ============================================================================

/**
 * Create new addon
 */
export function useCreateAddon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePlan) => addonService.createAddon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADDONS_QUERY_KEY] })
      toast.success('Addon created successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create addon'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update addon
 */
export function useUpdateAddon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePlan }) =>
      addonService.updateAddon(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ADDONS_QUERY_KEY] })
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.id],
      })
      toast.success('Addon updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update addon'
      toast.error(errorMessage)
    },
  })
}

/**
 * Delete addon
 */
export function useDeleteAddon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => addonService.deleteAddon(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADDONS_QUERY_KEY] })
      toast.success('Addon deleted successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete addon'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update addon status
 */
export function useUpdateAddonStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: EntityStatus }) =>
      addonService.updateStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ADDONS_QUERY_KEY] })
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.id],
      })
      toast.success('Addon status updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update addon status'
      toast.error(errorMessage)
    },
  })
}

// ============================================================================
// MUTATION HOOKS - PLAN CHARGES
// ============================================================================

/**
 * Add charge to plan
 */
export function useAddCharge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: string
      data: CreatePlanCharge
    }) => addonService.addCharge(planId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Charge added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add charge'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update charge
 */
export function useUpdateCharge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      chargeId,
      data,
    }: {
      planId: string
      chargeId: string
      data: UpdatePlanCharge
    }) => addonService.updateCharge(planId, chargeId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Charge updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update charge'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove charge from plan
 */
export function useRemoveCharge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ planId, chargeId }: { planId: string; chargeId: string }) =>
      addonService.removeCharge(planId, chargeId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Charge removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove charge'
      toast.error(errorMessage)
    },
  })
}

// ============================================================================
// MUTATION HOOKS - PLAN FEATURES
// ============================================================================

/**
 * Add feature to plan
 */
export function useAddFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: string
      data: CreatePlanFeature
    }) => addonService.addFeature(planId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Feature added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add feature'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update addon feature
 */
export function useUpdateFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
      data,
    }: {
      planId: string
      featureId: string
      data: UpdatePlanFeature
    }) => addonService.updateFeature(planId, featureId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Feature updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update feature'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove feature from plan
 */
export function useRemoveFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
    }: {
      planId: string
      featureId: string
    }) => addonService.removeFeature(planId, featureId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Feature removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove feature'
      toast.error(errorMessage)
    },
  })
}

/**
 * Add pricing tier to feature
 */
export function useAddFeatureTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
      data,
    }: {
      planId: string
      featureId: string
      data: CreatePricingTier
    }) => addonService.addFeatureTier(planId, featureId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add pricing tier'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update feature pricing tier
 */
export function useUpdateFeatureTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
      tierId,
      data,
    }: {
      planId: string
      featureId: string
      tierId: string
      data: UpdatePricingTier
    }) => addonService.updateFeatureTier(planId, featureId, tierId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update pricing tier'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove pricing tier from feature
 */
export function useRemoveFeatureTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
      tierId,
    }: {
      planId: string
      featureId: string
      tierId: string
    }) => addonService.removeFeatureTier(planId, featureId, tierId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove pricing tier'
      toast.error(errorMessage)
    },
  })
}

// ============================================================================
// MUTATION HOOKS - PLAN AGENT ACTIONS
// ============================================================================

/**
 * Add agent action to plan
 */
export function useAddAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: string
      data: CreatePlanAgentAction
    }) => addonService.addAgentAction(planId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Agent action added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add agent action'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update addon agent action
 */
export function useUpdateAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
      data,
    }: {
      planId: string
      agentActionId: string
      data: UpdatePlanAgentAction
    }) => addonService.updateAgentAction(planId, agentActionId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Agent action updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update agent action'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove agent action from plan
 */
export function useRemoveAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
    }: {
      planId: string
      agentActionId: string
    }) => addonService.removeAgentAction(planId, agentActionId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Agent action removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove agent action'
      toast.error(errorMessage)
    },
  })
}

/**
 * Add pricing tier to agent action
 */
export function useAddAgentActionTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
      data,
    }: {
      planId: string
      agentActionId: string
      data: CreatePricingTier
    }) => addonService.addAgentActionTier(planId, agentActionId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add pricing tier'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update agent action pricing tier
 */
export function useUpdateAgentActionTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
      tierId,
      data,
    }: {
      planId: string
      agentActionId: string
      tierId: string
      data: UpdatePricingTier
    }) =>
      addonService.updateAgentActionTier(planId, agentActionId, tierId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update pricing tier'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove pricing tier from agent action
 */
export function useRemoveAgentActionTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
      tierId,
    }: {
      planId: string
      agentActionId: string
      tierId: string
    }) => addonService.removeAgentActionTier(planId, agentActionId, tierId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADDONS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove pricing tier'
      toast.error(errorMessage)
    },
  })
}

// ============================================================================
// STUB HOOKS FOR LEGACY COMPONENTS (To be implemented later)
// ============================================================================

/**
 * Addon filters hook - converts UI filters to API query format
 */
export function useAddonFilters() {
  const [filters, setFilters] = useState<PlanFilters>({
    status: 'all',
    billingPeriod: 'all',
    targetAudience: 'all',
    type: 'all',
    page: 1,
    limit: 20,
  })

  const updateFilters = (newFilters: Partial<PlanFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  // Convert UI filters (camelCase) to API query (snake_case)
  const toAPIQuery = (uiFilters: PlanFilters): PlanQuery => {
    const query: PlanQuery = {}

    // Search
    if (uiFilters.search) {
      query.search = uiFilters.search
    }

    // Status (filter out 'all')
    if (uiFilters.status && uiFilters.status !== 'all') {
      query.status = uiFilters.status as EntityStatus
    }

    // Target Audience (filter out 'all')
    if (uiFilters.targetAudience && uiFilters.targetAudience !== 'all') {
      query.target_audience = uiFilters.targetAudience as TargetAudience
    }

    // Billing Period (convert UI values to API enum, filter out 'all')
    if (uiFilters.billingPeriod && uiFilters.billingPeriod !== 'all') {
      // Convert UI values to API enum values
      const periodMap: Record<string, BillingPeriod> = {
        monthly: 'monthly' as BillingPeriod,
        quarterly: 'quarterly' as BillingPeriod,
        yearly: 'annual' as BillingPeriod, // UI uses 'yearly', API expects 'annual'
      }
      query.billing_period =
        periodMap[uiFilters.billingPeriod] ||
        (uiFilters.billingPeriod as BillingPeriod)
    }

    // Category/Type (filter out 'all')
    if (uiFilters.type && uiFilters.type !== 'all') {
      query.category = uiFilters.type as PlanCategory
    }

    // Pagination
    if (uiFilters.page) {
      query.page = uiFilters.page
    }
    if (uiFilters.limit) {
      query.limit = uiFilters.limit
    }

    // Sorting
    if (uiFilters.sortBy) {
      query.order = uiFilters.sortOrder || 'desc'
    }

    return query
  }

  return {
    filters,
    updateFilters,
    setFilters,
    apiQuery: toAPIQuery(filters),
  }
}

// ============================================================================
// STUB HOOKS FOR LEGACY COMPONENTS
// ============================================================================

/**
 * Stub hook for addon charges - used by AddonDetail component
 * Separates charges into one-time vs recurring (memoized to prevent infinite render loops)
 */
export function useAddonCharges(addonId: string) {
  const { data: addon } = useAddon(addonId)

  return useMemo(() => {
    const charges = addon?.charges || []
    const oneTimeCharges = charges.filter(
      c => c.charge_type === CHARGE_TYPE.one_time
    )
    const recurringCharges = charges.filter(
      c => c.charge_type === CHARGE_TYPE.recurring
    )

    return {
      data: {
        oneTimeCharges,
        recurringCharges,
      },
      isLoading: false,
    }
  }, [addon?.charges])
}

/**
 * Stub hook for addon features - used by AddonDetail component
 * Extracts features array (memoized to prevent infinite render loops)
 */
export function useAddonFeatures(addonId: string) {
  const { data: addon } = useAddon(addonId)

  return useMemo(
    () => ({
      data: addon?.features || [],
      isLoading: false,
    }),
    [addon?.features]
  )
}

/**
 * Stub hook for addon trial settings - used by AddonPreviewCard component
 * Extracts trial configuration (memoized to prevent infinite render loops)
 */
export function useAddonTrial(addonId: string) {
  const { data: addon } = useAddon(addonId)

  return useMemo(
    () => ({
      data: {
        trial_available: addon?.trial_available || false,
        trial_days: addon?.trial_days || 0,
      },
      isLoading: false,
    }),
    [addon?.trial_available, addon?.trial_days]
  )
}
